<template>
	<div class="greeting">
		<div class="greeting__text">
			<h2 class="greeting__title sgnl-text-color-default sgnl-h6-medium">{{ greeting }}</h2>
			<div
				v-if="subtitleParts.length"
				class="greeting__subtitle sgnl-text-color-secondary sgnl-paragraph-s-regular"
			>
				<template
					v-for="(part, idx) in subtitleParts"
					:key="idx"
				>
					<span :class="part.class">{{ part.text }}</span>
				</template>
			</div>
		</div>
		<div class="greeting__refresh-wrap">
			<v-btn
				variant="outlined"
				class="greeting__refresh sgnl-text-color-secondary"
				size="default"
				:disabled="refreshing"
				@click="$emit('refresh')"
			>
				<Renew16
					class="greeting__refresh-icon"
					:class="{ 'greeting__refresh-icon--spinning': refreshing }"
				/>
				Refresh
			</v-btn>
			<div
				v-if="lastRefreshedLabel"
				class="greeting__last-refreshed sgnl-text-color-tertiary sgnl-footnote-regular"
				data-test="greeting-last-refreshed"
			>
				{{ lastRefreshedLabel }}
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Renew16 from "@carbon/icons-vue/es/renew/16";
import { formatElapsedMmSs } from "@/js/utilities";

type RunningTask = { id: string; core: string; startedMs: number };
type LastTask = { whenLabel: string; core: string };

type SubtitlePart = { text: string; class?: string };

const prefixForHour = (h: number): string => {
	if (h < 5) return "Good evening";
	if (h < 12) return "Good morning";
	if (h < 18) return "Good afternoon";
	return "Good evening";
};

export default defineComponent({
	name: "HomeGreeting",
	components: { Renew16 },
	props: {
		firstName: { type: String as PropType<string | null>, default: null },
		running: { type: Array as PropType<RunningTask[]>, default: () => [] },
		lastTask: { type: Object as PropType<LastTask | null>, default: null },
		hasApiKey: { type: Boolean, default: false },
		tasksRun: { type: Number, default: 0 },
		refreshing: { type: Boolean, default: false },
		lastRefreshedLabel: { type: String, default: "" },
		// Shared 1s ticker owned by Home.vue; avoids a duplicate setInterval here.
		now: { type: Number, required: true },
	},
	emits: ["refresh"],
	computed: {
		greeting(): string {
			const prefix = prefixForHour(new Date(this.now).getHours());
			return this.firstName ? `${prefix}, ${this.firstName}` : prefix;
		},
		subtitleParts(): SubtitlePart[] {
			if (this.running.length === 1) {
				const t = this.running[0];
				const elapsed = t.startedMs ? formatElapsedMmSs(this.now - t.startedMs) : "";
				const parts: SubtitlePart[] = [{ text: "Running on " }, { text: t.core, class: "greeting__mono" }];
				if (elapsed) parts.push({ text: " · " }, { text: `${elapsed} elapsed`, class: "greeting__brand" });
				return parts;
			}
			if (this.running.length > 1) {
				// Smallest startedMs = earliest start = longest running.
				const longest = this.running.reduce((a, b) => {
					if (!b.startedMs) return a;
					if (!a.startedMs) return b;
					return a.startedMs < b.startedMs ? a : b;
				});
				const longestElapsed = longest.startedMs ? formatElapsedMmSs(this.now - longest.startedMs) : "";
				const parts: SubtitlePart[] = [
					{ text: `${this.running.length}`, class: "greeting__strong" },
					{ text: " tasks running" },
				];
				if (longestElapsed) {
					parts.push(
						{ text: " · longest " },
						{ text: longestElapsed, class: "greeting__brand" },
						{ text: " on " },
						{ text: longest.core, class: "greeting__mono" },
					);
				}
				return parts;
			}
			if (this.lastTask) {
				return [
					{ text: "Last task " },
					{ text: this.lastTask.whenLabel, class: "greeting__brand" },
					{ text: " on " },
					{ text: this.lastTask.core, class: "greeting__mono" },
				];
			}
			if (!this.hasApiKey) {
				return [{ text: "Set up your workspace to get started." }];
			}
			if (this.tasksRun === 0) {
				return [{ text: "No tasks run yet — pick something below to kick off your first run." }];
			}
			return [];
		},
	},
});
</script>

<style lang="scss" scoped>
.greeting {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: var(--sgnl-space-12);
}

.greeting__text {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
	min-width: 0;
}

.greeting__title {
	margin: 0;
}

.greeting__mono {
	font-family: var(--sgnl-font-family-mono);
}
.greeting__brand {
	color: var(--sgnl-brand-text);
}
.greeting__strong {
	color: var(--sgnl-default-primary-text);
	font-weight: 500;
}

.greeting__refresh-wrap {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: var(--sgnl-space-4);
}

.greeting__refresh {
	border-color: var(--sgnl-default-primary-border);
}
.greeting__refresh :deep(.v-btn__content) {
	gap: var(--sgnl-space-8);
}
.greeting__refresh :deep(svg) {
	color: var(--sgnl-default-secondary-icon);
}

@keyframes greeting-refresh-spin {
	to {
		transform: rotate(360deg);
	}
}
.greeting__refresh-icon--spinning {
	animation: greeting-refresh-spin 800ms linear infinite;
	transform-origin: center;
}

@media (max-width: 719px) {
	.greeting {
		flex-direction: column;
		align-items: flex-start;
	}
}
</style>
